import { useEffect, useMemo, useState } from "react";
import { Building2, ChevronDown, Loader2, Package, Search, User } from "lucide-react";
import {
  getContestSummaryProducts,
  ContestSummaryProductItem,
  ContestSummaryProductsResponse,
} from "@/Api/stocks";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Dialog } from "@/components/ui/dialog";
import { AppDialogBody, AppDialogContent } from "@/components/ui/app-dialog";
import { cn } from "@/lib/utils";

type SortMode = "quantity" | "number";

interface SummaryProductsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contestId: number | null;
  productClassificationId: number | null;
  /** Descrição da linha do resumo, ex.: "1jg X 20d X 39ct - R$ 1.878,36" */
  description: string;
  /** Unidade do "Resumo por Unidade"; vazio no "Resumo Geral" */
  unityId?: number | null;
  unityName?: string | null;
  contestName?: string;
  lotteryName?: string;
  lotteryColor?: string;
}

const sortProducts = (
  products: ContestSummaryProductItem[],
  mode: SortMode
): ContestSummaryProductItem[] =>
  [...products].sort((a, b) => {
    if (mode === "quantity" && b.balance !== a.balance) {
      return b.balance - a.balance;
    }
    return a.product_number - b.product_number;
  });

const formatCotas = (quantity: number) => `${quantity} ${quantity === 1 ? "cota" : "cotas"}`;

export const SummaryProductsModal = ({
  open,
  onOpenChange,
  contestId,
  productClassificationId,
  description,
  unityId,
  unityName,
  contestName,
  lotteryName,
  lotteryColor,
}: SummaryProductsModalProps) => {
  const [data, setData] = useState<ContestSummaryProductsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>("quantity");
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!open || !contestId || !productClassificationId) return;

    let cancelled = false;

    setData(null);
    setError(null);
    setExpanded(new Set());
    setSearchTerm("");
    setSortMode("quantity");
    setLoading(true);

    // Em caso de erro o interceptor do axios já exibe o toast e devolve ok = false
    getContestSummaryProducts(contestId, productClassificationId, unityId || undefined)
      .then((response) => {
        if (cancelled) return;
        if (!response?.ok || !response.data?.response) {
          setError("Não foi possível carregar os bolões desta linha do resumo");
          return;
        }
        setData(response.data.response);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching summary products:", err);
        setError("Não foi possível carregar os bolões desta linha do resumo");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, contestId, productClassificationId, unityId]);

  const visibleProducts = useMemo(() => {
    if (!data) return [];

    const sorted = sortProducts(data.products, sortMode);
    const term = searchTerm.replace(/\D/g, "");

    return term
      ? sorted.filter((product) => String(product.product_number).startsWith(term))
      : sorted;
  }, [data, sortMode, searchTerm]);

  const toggleProduct = (cpClassificationId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(cpClassificationId)) {
        next.delete(cpClassificationId);
      } else {
        next.add(cpClassificationId);
      }
      return next;
    });
  };

  const sortButtonClass = (mode: SortMode) =>
    cn(
      "px-4 py-2 rounded-md border text-xs font-semibold uppercase tracking-wide transition-colors",
      sortMode === mode
        ? "bg-primary border-primary text-white shadow-sm"
        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100 dark:bg-zinc-900 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
    );

  const subtitle = [lotteryName && contestName ? `${lotteryName} ${contestName}` : contestName, unityName]
    .filter(Boolean)
    .join(" · ");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <AppDialogContent
        icon={Package}
        title="Bolões da linha"
        description={subtitle || undefined}
        className="sm:max-w-2xl"
      >
        <AppDialogBody className="gap-4">
          <div className="flex flex-wrap items-center gap-2">
            {lotteryName && (
              <Badge
                className="text-xs text-white border-0 uppercase"
                style={{ backgroundColor: lotteryColor || "#6B7280" }}
              >
                {lotteryName}
              </Badge>
            )}
            {contestName && <span className="font-semibold text-lg">{contestName}</span>}
            <span className="text-xs font-medium text-amber-800 bg-amber-100 dark:text-amber-200 dark:bg-amber-900/30 rounded-md px-2 py-1">
              ({data?.product_classification.name || description})
            </span>
            {unityName && (
              <span className="flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 rounded-md px-2 py-1">
                <Building2 className="h-3.5 w-3.5" />
                {unityName}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={sortButtonClass("quantity")}
                onClick={() => setSortMode("quantity")}
              >
                Ordenar por quantidade
              </button>
              <button
                type="button"
                className={sortButtonClass("number")}
                onClick={() => setSortMode("number")}
              >
                Ordenar por ordem numérica
              </button>
            </div>
            <div className="relative w-full sm:w-44">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-primary" />
              <Input
                type="text"
                inputMode="numeric"
                placeholder="Buscar bolão (B)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value.replace(/\D/g, ""))}
                className="pl-8 h-9 bg-white dark:bg-zinc-900"
              />
            </div>
          </div>

          {data && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                {data.summary.total_products}{" "}
                {data.summary.total_products === 1 ? "bolão" : "bolões"}
              </span>
              <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-300">
                {formatCotas(data.summary.total_balance)}
              </span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <p className="text-center text-red-600 dark:text-red-400 py-8">{error}</p>
          ) : !data || data.products.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhum bolão com cotas nesta linha do resumo
            </p>
          ) : visibleProducts.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhum bolão encontrado com esse número
            </p>
          ) : (
            <div className="space-y-2">
              {visibleProducts.map((product) => {
                const isExpanded = expanded.has(product.cp_classification_id);

                return (
                  <Collapsible
                    key={product.cp_classification_id}
                    className={cn(
                      "border border-l-4 border-l-primary rounded-lg bg-white dark:bg-zinc-800 transition-colors",
                      isExpanded && "bg-primary/5 dark:bg-primary/10"
                    )}
                    open={isExpanded}
                    onOpenChange={() => toggleProduct(product.cp_classification_id)}
                  >
                    <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-primary/5 transition-colors rounded-lg">
                      <div className="flex items-center gap-4">
                        <span className="font-bold text-xl min-w-[64px] text-left text-primary dark:text-blue-300">
                          {product.product_name}
                        </span>
                        <Badge className="text-sm bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300">
                          {formatCotas(product.balance)}
                        </Badge>
                      </div>
                      <ChevronDown
                        className={cn(
                          "h-4 w-4 text-primary transition-transform duration-200",
                          isExpanded && "rotate-180"
                        )}
                      />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="px-4 pb-4 space-y-3">
                      {product.locations.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          Sem detalhamento de onde estão as cotas
                        </p>
                      ) : (
                        product.locations.map((location) => (
                          <div
                            key={location.unity_id ?? "sem-unidade"}
                            className="rounded-md border border-primary/20 bg-white dark:bg-zinc-900 overflow-hidden"
                          >
                            <div className="flex items-center justify-between px-3 py-2 bg-primary/10 dark:bg-primary/20">
                              <span className="flex items-center gap-2 text-sm font-semibold text-primary dark:text-blue-200">
                                <Building2 className="h-4 w-4" />
                                {location.unity_name}
                              </span>
                              <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-bold text-green-700 dark:bg-green-900/30 dark:text-green-300">
                                {formatCotas(location.total_balance)}
                              </span>
                            </div>
                            <div className="divide-y divide-gray-100 dark:divide-gray-700">
                              {location.unity_balance > 0 && (
                                <div className="flex items-center justify-between px-3 py-1.5 text-sm bg-amber-50/60 dark:bg-amber-900/10">
                                  <span className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                                    <Package className="h-3.5 w-3.5" />
                                    No estoque da unidade
                                  </span>
                                  <span className="font-semibold text-amber-700 dark:text-amber-300">
                                    {formatCotas(location.unity_balance)}
                                  </span>
                                </div>
                              )}
                              {location.sellers.map((seller) => (
                                <div
                                  key={seller.seller_id}
                                  className="flex items-center justify-between px-3 py-1.5 text-sm"
                                >
                                  <span className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                                    <User className="h-3.5 w-3.5 text-purple-600" />
                                    {seller.seller_name}
                                  </span>
                                  <span className="font-semibold text-green-700 dark:text-green-300">
                                    {formatCotas(seller.balance)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </AppDialogBody>
      </AppDialogContent>
    </Dialog>
  );
};

export default SummaryProductsModal;
